import React, { useRef, useEffect } from "react";
import { useAdmin } from "./adminShared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RotateCcw, Columns3 } from "lucide-react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Convert hex/rgba to rgba string with a given alpha (for previews)
const toRgba = (color, alpha) => {
  if (!color) return color;
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const m = color.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const [r, g, b] = m[1].split(",").map((s) => parseFloat(s));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
};

export const VotingColumnEditor = () => {
  const {
    votingColumnCenter,
    votingWidthCustom, setVotingWidthCustom, votingWidthSpan, setVotingWidthSpan,
    voteBarColorMode, setVoteBarColorMode, voteBarColor1, setVoteBarColor1, voteBarColor2, setVoteBarColor2,
    voteBarTranslucent, setVoteBarTranslucent, voteBarOpacity, setVoteBarOpacity,
  } = useAdmin();

  const stripRef = useRef(null);
  const draggingRef = useRef(false);

  const W = votingWidthSpan % 2 === 0 ? votingWidthSpan : Math.max(2, votingWidthSpan - 1);
  // Both leaderboard columns are ALWAYS equal: (12 - even width) / 2.
  const left = (12 - W) / 2;
  const right = (12 - W) / 2;
  // Whether the voting column sits on the right side (default) or centered.
  const isSide = !votingColumnCenter;

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !stripRef.current) return;
      const rect = stripRef.current.getBoundingClientRect();
      const unit = rect.width / 12;
      const newW = isSide
        // Votes are on the RIGHT: width = distance from the right edge to cursor.
        ? Math.round((rect.right - e.clientX) / unit)
        // Votes are CENTERED: symmetric width = 2 x distance from center.
        : Math.round((2 * (e.clientX - (rect.left + rect.width / 2))) / unit);
      // Snap to even so the two leaderboard columns stay equal.
      const evenW = Math.round(newW / 2) * 2;
      setVotingWidthSpan(clamp(evenW, 2, 8));
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [setVotingWidthSpan, isSide]);

  const startDrag = (e) => { e.preventDefault(); draggingRef.current = true; };

  const previewBarBg = (isEven) => {
    if (voteBarColorMode === "original") return isEven ? "rgba(24, 24, 27, 0.9)" : "rgba(39, 39, 42, 0.7)";
    const base = voteBarColorMode === "dual" ? (isEven ? voteBarColor1 : voteBarColor2) : voteBarColor1;
    const alpha = (voteBarTranslucent ? 0.25 : 1) * (voteBarOpacity / 100);
    return toRgba(base, alpha);
  };

  const sampleVotes = [["1º país", 12], ["2º país", 10], ["3º país", 8]];

  const marcadorBlock = (flex, key) => (
    <div key={key} className="rounded bg-white/10 border border-white/10 flex items-center justify-center text-[10px] text-zinc-400" style={{ flex }}>Marcador</div>
  );

  const votesBlock = (handleSide) => (
    <div className="relative rounded bg-pink-600/30 border border-pink-500/50 flex items-center justify-center text-[10px] text-pink-200 font-semibold" style={{ flex: W }}>
      Votos
      <div
        onPointerDown={startDrag}
        data-testid="voting-width-handle"
        className={`absolute ${handleSide === "left" ? "-left-1.5" : "-right-1.5"} top-1/2 -translate-y-1/2 w-4 h-10 rounded bg-pink-500 border-2 border-white cursor-ew-resize shadow flex items-center justify-center`}
        title="Arrastra para cambiar el ancho"
      >
        <div className="w-0.5 h-4 bg-white/80 rounded" />
      </div>
    </div>
  );

  return (
    <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700 space-y-5" data-testid="voting-column-editor">
      <div className="flex items-center gap-2">
        <Columns3 className="w-4 h-4 text-pink-400" />
        <span className="text-zinc-200 font-semibold">Columna de votación (país que muestra sus votos)</span>
      </div>
      <p className="text-zinc-500 text-xs -mt-2">
        Tu marcador está dividido en dos columnas y la columna de votos está{" "}
        <span className="text-pink-300 font-semibold">{isSide ? "a la derecha" : "en el centro"}</span>.{" "}
        {isSide
          ? "Cambia la posición (derecha/centro) en \"Distribución del marcador\"."
          : "Cambia la posición en \"Distribución del marcador\"."}
      </p>

      {/* ---- ANCHO ---- */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-zinc-300 text-sm font-semibold">Ancho de la columna de votos</Label>
          <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-0.5 border border-zinc-700">
            <button
              type="button"
              data-testid="voting-width-original-btn"
              onClick={() => setVotingWidthCustom(false)}
              className={`px-3 py-1 rounded-md text-xs transition-colors ${!votingWidthCustom ? "bg-pink-600 text-white" : "text-zinc-400 hover:text-white"}`}
            >Original</button>
            <button
              type="button"
              data-testid="voting-width-custom-btn"
              onClick={() => setVotingWidthCustom(true)}
              className={`px-3 py-1 rounded-md text-xs transition-colors ${votingWidthCustom ? "bg-pink-600 text-white" : "text-zinc-400 hover:text-white"}`}
            >Personalizado</button>
          </div>
        </div>

        {!votingWidthCustom ? (
          <p className="text-zinc-500 text-xs">Se usa el ancho actual del marcador (sin cambios). Pulsa &quot;Personalizado&quot; para probar otros anchos.</p>
        ) : (
          <>
            <p className="text-zinc-500 text-xs mb-2">
              Arrastra el tirador para ensanchar/estrechar la columna de votos. Las dos columnas del marcador se adaptan automáticamente.
            </p>
            {/* Draggable preview strip — refleja el layout real */}
            <div ref={stripRef} className="relative w-full h-24 rounded-lg overflow-hidden border border-zinc-600 flex gap-1 bg-[#09090B] p-1 select-none touch-none" data-testid="voting-width-strip">
              {isSide ? (
                <>
                  {marcadorBlock(left, "ml")}
                  {marcadorBlock(right, "mr")}
                  {votesBlock("left")}
                </>
              ) : (
                <>
                  {marcadorBlock(left, "ml")}
                  {votesBlock("right")}
                  {marcadorBlock(right, "mr")}
                </>
              )}
            </div>

            <div className="flex items-center gap-3 mt-2">
              <input
                type="range" min="2" max="8" step="2" value={W}
                onChange={(e) => setVotingWidthSpan(Number(e.target.value))}
                className="flex-1 accent-pink-500"
                data-testid="voting-width-slider"
              />
              <span className="text-zinc-400 text-xs w-28 text-right">Votos: {W}/12 · Marcador: {left}+{right}</span>
            </div>

            <Button
              type="button" variant="outline" size="sm"
              onClick={() => setVotingWidthCustom(false)}
              className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700"
              data-testid="voting-width-reset-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Volver al ancho original
            </Button>
          </>
        )}
      </div>

      {/* ---- COLOR DE LAS BARRAS DE VOTACIÓN ---- */}
      <div className="pt-4 border-t border-zinc-700">
        <Label className="text-zinc-300 text-sm font-semibold mb-2 block">Color de las barras de votación</Label>
        <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-0.5 border border-zinc-700 w-fit mb-3">
          {[["original", "Original"], ["single", "Un color"], ["dual", "Dos colores"]].map(([val, label]) => (
            <button
              key={val}
              type="button"
              data-testid={`vote-bar-color-mode-${val}`}
              onClick={() => setVoteBarColorMode(val)}
              className={`px-3 py-1 rounded-md text-xs transition-colors ${voteBarColorMode === val ? "bg-pink-600 text-white" : "text-zinc-400 hover:text-white"}`}
            >{label}</button>
          ))}
        </div>

        {voteBarColorMode !== "original" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4">
              <div>
                <Label className="text-zinc-400 text-xs mb-1 block">{voteBarColorMode === "dual" ? "Color filas pares" : "Color"}</Label>
                <input type="color" value={voteBarColor1} onChange={(e) => setVoteBarColor1(e.target.value)} className="w-14 h-9 rounded cursor-pointer border border-zinc-700" data-testid="vote-bar-color-1" />
              </div>
              {voteBarColorMode === "dual" && (
                <div>
                  <Label className="text-zinc-400 text-xs mb-1 block">Color filas impares</Label>
                  <input type="color" value={voteBarColor2} onChange={(e) => setVoteBarColor2(e.target.value)} className="w-14 h-9 rounded cursor-pointer border border-zinc-700" data-testid="vote-bar-color-2" />
                </div>
              )}
              <div className="flex items-end">
                <button
                  type="button"
                  data-testid="vote-bar-translucent-toggle"
                  onClick={() => setVoteBarTranslucent(!voteBarTranslucent)}
                  className={`text-xs px-3 py-2 rounded border ${voteBarTranslucent ? "border-cyan-500/50 text-cyan-400 bg-cyan-950/30" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`}
                >{voteBarTranslucent ? "Translúcido" : "Opaco"}</button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1 block">Opacidad: {voteBarOpacity}%</Label>
              <input type="range" min="10" max="100" value={voteBarOpacity} onChange={(e) => setVoteBarOpacity(Number(e.target.value))} className="w-full accent-purple-500" data-testid="vote-bar-opacity" />
            </div>
          </div>
        )}

        {/* Live preview of vote bars */}
        <div className="mt-3">
          <p className="text-zinc-500 text-xs mb-1">Vista previa:</p>
          <div className="rounded-lg p-2 bg-[#09090B] border border-zinc-700 space-y-1 max-w-xs">
            {sampleVotes.map(([name, pts], i) => (
              <div key={name} className="rounded-lg p-2 border border-white/10 flex items-center justify-between" style={{ backgroundColor: previewBarBg(i % 2 === 0) }}>
                <span className="text-white text-xs">{name}</span>
                <span className="text-pink-300 text-sm font-bold">{pts}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
